import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL, fmtTRY, fmtDate, fmtDateTime } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Wrench, ShoppingCart, Phone, MapPin, User as UserIcon, CheckCircle2, Clock, Package, Receipt, Wallet } from "lucide-react";

const WO_STATUS = {
  kabul: { label: "Kabul Edildi", color: "bg-slate-500 text-white", desc: "İş emri açıldı, incelemeye alındı." },
  parca_bekliyor: { label: "Parça Bekliyor", color: "bg-amber-500 text-slate-900", desc: "Gerekli parça temin ediliyor." },
  onarimda: { label: "Onarımda", color: "bg-[#EA580C] text-white", desc: "Aracınız onarım işlemi görüyor." },
  test: { label: "Test Edildi", color: "bg-blue-500 text-white", desc: "Onarım tamamlandı, teste alındı." },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white", desc: "Aracınız teslim edildi." },
};

const ORDER_STATUS = {
  bekliyor: { label: "Bekliyor", color: "bg-amber-500 text-slate-900", desc: "Siparişiniz alındı." },
  hazirlaniyor: { label: "Hazırlanıyor", color: "bg-blue-500 text-white", desc: "Sipariş hazırlanıyor." },
  hazir: { label: "Hazır", color: "bg-[#D97706] text-white", desc: "Sipariş teslime hazır." },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white", desc: "Sipariş teslim edildi." },
  iptal: { label: "İptal", color: "bg-slate-400 text-white", desc: "Sipariş iptal edildi." },
};

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/portal/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Portal bulunamadı"));
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center p-6 bg-slate-50"><Card className="max-w-md"><CardContent className="p-6 text-center"><div className="text-4xl mb-3">🔒</div><div className="text-lg font-bold text-slate-900">{err}</div><div className="text-sm text-slate-500 mt-1">Doğru link ile tekrar deneyin veya bayinizle iletişime geçin.</div></CardContent></Card></div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Yükleniyor…</div>;

  const primary = data.company?.primary_color || "#1B4D3E";
  const secondary = data.company?.secondary_color || "#D97706";
  const openWO = data.work_orders.filter((w) => w.status !== "teslim").length;
  const openOrders = data.part_orders.filter((o) => !["teslim", "iptal"].includes(o.status)).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200" style={{ background: primary }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-extrabold" style={{ background: secondary }}>
              {(data.company?.name || "TP").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-white font-extrabold text-lg">{data.company?.name || "TraktörPro"}</div>
              <div className="text-white/70 text-xs">Müşteri Portalı</div>
            </div>
          </div>
          <div className="text-right text-white text-sm">
            <div className="font-semibold flex items-center gap-1 justify-end"><UserIcon size={14} />{data.customer.name}</div>
            {data.customer.phone && <div className="text-white/70 text-xs flex items-center gap-1 justify-end"><Phone size={12} />{data.customer.phone}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
            <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Açık Servis</div><div className="text-2xl font-extrabold text-slate-900">{openWO}</div></div>
            <div className="h-10 w-10 rounded-xl text-white grid place-items-center" style={{ background: primary }}><Wrench size={18} /></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center justify-between">
            <div><div className="text-xs uppercase tracking-widest font-bold text-slate-500">Açık Sipariş</div><div className="text-2xl font-extrabold text-slate-900">{openOrders}</div></div>
            <div className="h-10 w-10 rounded-xl text-white grid place-items-center" style={{ background: secondary }}><ShoppingCart size={18} /></div>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="service">
          <TabsList>
            <TabsTrigger value="service"><Wrench size={14} className="mr-1" /> Servis</TabsTrigger>
            <TabsTrigger value="orders"><ShoppingCart size={14} className="mr-1" /> Sipariş</TabsTrigger>
            <TabsTrigger value="notes"><Receipt size={14} className="mr-1" /> Senetler</TabsTrigger>
            <TabsTrigger value="balance"><Wallet size={14} className="mr-1" /> Bakiye</TabsTrigger>
          </TabsList>

          <TabsContent value="service">
            <div className="space-y-3">
              {data.work_orders.map((w) => (
                <Card key={w.wo_no} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-mono text-xs text-slate-500">{w.wo_no}</div>
                        <div className="font-bold text-slate-900 mt-0.5">{w.tractor_brand} {w.tractor_model} {w.plate && `· ${w.plate}`}</div>
                        {w.complaint && <div className="text-sm text-slate-600 mt-1">{w.complaint}</div>}
                        <div className="text-xs text-slate-500 mt-1">{fmtDate(w.created_at)}{w.delivered_at && ` → Teslim: ${fmtDate(w.delivered_at)}`}</div>
                      </div>
                      <div className="text-right">
                        <Badge className={WO_STATUS[w.status]?.color}>{WO_STATUS[w.status]?.label}</Badge>
                        {w.status === "teslim" && <div className="text-lg font-extrabold mt-1" style={{ color: primary }}>{fmtTRY(w.total)}</div>}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">{WO_STATUS[w.status]?.desc}</div>
                    {/* pipeline */}
                    <div className="flex items-center gap-1 mt-3">
                      {["kabul", "parca_bekliyor", "onarimda", "test", "teslim"].map((s, idx) => {
                        const activeIdx = ["kabul", "parca_bekliyor", "onarimda", "test", "teslim"].indexOf(w.status);
                        return (
                          <div key={s} className="flex-1 flex items-center gap-1">
                            <div className="h-1.5 flex-1 rounded-full" style={{ background: idx <= activeIdx ? primary : "#e2e8f0" }} />
                            {idx < 4 && <div className="h-2 w-2 rounded-full" style={{ background: idx <= activeIdx ? primary : "#e2e8f0" }} />}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {data.work_orders.length === 0 && <div className="text-center py-10 text-slate-500">Servis kaydı yok.</div>}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="space-y-3">
              {data.part_orders.map((o) => (
                <Card key={o.order_no} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-mono text-xs text-slate-500">{o.order_no}</div>
                        <div className="font-bold text-slate-900 mt-0.5">{o.items?.length || 0} kalem sipariş</div>
                        <div className="text-xs text-slate-500 mt-1">{fmtDate(o.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <Badge className={ORDER_STATUS[o.status]?.color}>{ORDER_STATUS[o.status]?.label}</Badge>
                        <div className="text-lg font-extrabold mt-1" style={{ color: primary }}>{fmtTRY(o.total)}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">{ORDER_STATUS[o.status]?.desc}</div>
                    <ul className="mt-2 space-y-1 text-sm">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="flex justify-between text-slate-600 text-xs">
                          <span>{it.name} × {it.quantity}</span>
                          <span className="font-mono">{fmtTRY(Number(it.quantity) * Number(it.unit_price))}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
              {data.part_orders.length === 0 && <div className="text-center py-10 text-slate-500">Sipariş kaydı yok.</div>}
            </div>
          </TabsContent>

          <TabsContent value="notes">
            <div className="space-y-3">
              {(data.notes || []).map((n, i) => (
                <Card key={i} className="border-slate-200">
                  <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="uppercase">{n.note_type || "senet"}</Badge>
                        {n.status === "tahsil" && <Badge className="bg-emerald-600 text-white">Tahsil Edildi</Badge>}
                        {n.overdue && <Badge className="bg-rose-600 text-white">+{n.overdue_days} gün gecikti</Badge>}
                        {n.status === "bekliyor" && !n.overdue && <Badge className="bg-amber-500 text-slate-900">Vadesi Yaklaşıyor</Badge>}
                      </div>
                      <div className="text-sm text-slate-700 mt-1"><span className="text-xs text-slate-500">Vade:</span> <span className="font-semibold">{fmtDate(n.due_date)}</span></div>
                      {n.notes && <div className="text-xs text-slate-500 mt-1">{n.notes}</div>}
                      {n.paid_at && <div className="text-xs text-emerald-700 mt-1">Ödendi: {fmtDate(n.paid_at)} · {fmtTRY(n.paid_amount || n.amount)}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Tutar</div>
                      <div className="text-2xl font-extrabold" style={{ color: primary }}>{fmtTRY(n.amount)}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(!data.notes || data.notes.length === 0) && <div className="text-center py-10 text-slate-500">Senet kaydı yok.</div>}
            </div>
          </TabsContent>

          <TabsContent value="balance">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-slate-900 text-white rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Toplam Borç</div>
                    <div className="text-2xl font-extrabold mt-1">{fmtTRY(data.balance?.borc || 0)}</div>
                    <div className="text-xs text-slate-300 mt-1">Satış + servis + sipariş</div>
                  </div>
                  <div className="bg-emerald-600 text-white rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">Toplam Ödeme</div>
                    <div className="text-2xl font-extrabold mt-1">{fmtTRY(data.balance?.alacak || 0)}</div>
                    <div className="text-xs text-emerald-100 mt-1">Tahsilatlar</div>
                  </div>
                  <div className={`rounded-xl p-4 text-white`} style={{ background: (data.balance?.net || 0) > 0 ? "#E11D48" : primary }}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">Bakiye</div>
                    <div className="text-2xl font-extrabold mt-1">{fmtTRY(Math.abs(data.balance?.net || 0))}</div>
                    <div className="text-xs text-white/80 mt-1">{(data.balance?.net || 0) > 0 ? "Borçlu bakiye" : (data.balance?.net || 0) < 0 ? "Alacaklı bakiye" : "Bakiye sıfır"}</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-4">
                  Not: Bakiye toplam borç ve ödeme tutarları üzerinden hesaplanır. Detaylı hareket dökümü için lütfen bayinizle iletişime geçin.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center text-xs text-slate-400 mt-8">
          Powered by <span className="font-bold" style={{ color: primary }}>{data.company?.name || "TraktörPro"}</span>
          {data.company?.phone && <> · <Phone size={10} className="inline" /> {data.company.phone}</>}
        </div>
      </main>
    </div>
  );
}
